import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { GlassLabel } from '../GlassLabel'
import { EmotionType } from '../../../../types/FeedbackTypes'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { getEmotionConfig } from '../../config/emotionConfig.tsx'
import { EmotionSphere } from './EmotionSphere'

export interface EmotionVisualizationProps {
  /**
   * Emotion to display
   */
  emotion: EmotionType | string
  /**
   * Title for the emotion section
   */
  title?: string
  /**
   * Variant for team or individual styling
   */
  variant?: 'team' | 'individual'
  /**
   * Optional example utterance showing the emotion
   */
  exampleUtterance?: string

  /**
   * Optional generated text describing the communicated emotional tone
   */
  text?: string
}

/**
 * EmotionVisualization - Displays emotion with custom SVG face
 *
 * Features:
 * - Custom SVG faces for each emotion
 * - Concentric circles with emotion-specific gradients
 * - Fixed orange glow effect (INTERAX_COLORS.sun)
 * - Glass label at bottom with translated emotion name
 * - Clean, modular design
 */
const EmotionVisualization: React.FC<EmotionVisualizationProps> = ({
  emotion,
  title,
  variant: _variant, // Reserved for future background color variations
  text
}) => {
  const { t } = useTranslation()

  // Get emotion configuration and translated label
  const emotionConfig = getEmotionConfig(emotion as EmotionType)
  const emotionLabel = t(emotionConfig.labelKey)
  const displayTitle = title || t('gamer.feedback.emotions.title')

  // Handle empty state
  if (!emotion || emotion.trim() === '') {
    return (
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          gap: '16px',
          isolation: 'isolate',
          position: 'relative',
          width: '100%',
          height: '308px',
          background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
          backgroundBlendMode: 'soft-light, normal',
          border: `1px solid ${INTERAX_COLORS.white04}`,
          boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
          borderRadius: '10px',
          overflow: 'hidden'
        }}
      >
        {/* Orange glow ellipse */}
        <Box
          sx={{
            position: 'absolute',
            width: '244px',
            height: '177px',
            left: '31.5px',
            top: '-120px',
            background: INTERAX_COLORS.sun,
            filter: 'blur(75px)',
            zIndex: 0
          }}
        />

        {/* Title at top */}
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.headline5,
            color: INTERAX_COLORS.white,
            position: 'absolute',
            top: '24px',
            left: '24px',
            right: '24px',
            textAlign: 'center',
            zIndex: 1
          }}
        >
          {displayTitle}
        </Typography>

        {/* No data message in center */}
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body2,
            color: INTERAX_COLORS.thunderGrey,
            textAlign: 'center',
            zIndex: 1
          }}
        >
          {t('gamer.feedback.emotion.noData')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        // Container - 307px x 308px on desktop, responsive on mobile
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: { xs: '12px', sm: '16px', md: '24px' },
        gap: { xs: '10px', sm: '12px', md: '24px' },
        isolation: 'isolate',
        position: 'relative',
        width: '100%',
        height: { xs: 'auto', sm: 'auto' },
        minHeight: { xs: 'auto', sm: 'auto', md: '308px' },
        background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
        backgroundBlendMode: 'soft-light, normal',
        border: `1px solid ${INTERAX_COLORS.white04}`,
        boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        overflow: 'hidden'
      }}
    >
      {/* Orange glow ellipse - Fixed color for all emotions */}
      <Box
        sx={{
          position: 'absolute',
          width: '244px',
          height: '177px',
          left: '31.5px',
          top: '-120px',
          background: INTERAX_COLORS.sun,
          filter: 'blur(75px)',
          zIndex: 0
        }}
      />

      {/* Title */}
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.headline5,
          color: INTERAX_COLORS.white,
          zIndex: 1,
          alignSelf: 'stretch'
        }}
      >
        {displayTitle}
      </Typography>

      {/* Emotion Sphere with custom SVG face */}
      <Box
        sx={{
          zIndex: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <EmotionSphere emotionConfig={emotionConfig} />
      </Box>

      {/* Glass label with translated emotion name */}
      <GlassLabel label={emotionLabel} sx={{ zIndex: 3 }} />

      {/* Example utterance quote in glass box (if provided) */}
      {/*Change to default text instead of real exampleUtterance */}

      <Box
        sx={{
          zIndex: 3,
          width: '100%',
          padding: '12px 16px',
          background: 'rgba(255, 255, 255, 0.1)',
          border: `1px solid ${INTERAX_COLORS.white08}`,
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2), inset 0px 1px 0px rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(6px)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body4,
            color: INTERAX_COLORS.white,
            textAlign: 'center',
            fontStyle: 'italic',
            lineHeight: '20px',
            width: '100%',
            minWidth: 0,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal'
          }}
        >
          {t('gamer.feedback.emotion.exampleUtteranceLabel', {
            defaultValue: 'Your strongest communicated emotion over the session.'
          })}
        </Typography>
      </Box>

      {/* Generated communicated emotion text (if provided) */}
      {text && text.trim() && (
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body4,
            color: INTERAX_COLORS.thunderGrey,
            textAlign: 'center',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 6,
            WebkitBoxOrient: 'vertical',
            width: '100%',
            minWidth: 0,
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {text}
        </Typography>
      )}
    </Box>
  )
}

export default EmotionVisualization
